import React from 'react';
import cx from "classnames";
import Image from 'next/image';
import Link  from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import Accordion from "@/components/Accordion";
import AnalyticsLink from '@/components/AnalyticsLink';
import Button from '@/components/Button';
import StandardCard from '@/components/cards/StandardCard';
import { useGetUrl } from '@/hooks/getUrl';
import { useTranslation } from '@/hooks/translation';
import { DoubleQuote } from '@/icons';
import { Banking, Fast, Group, Money, Security } from "@/icons/podercard";
import { csLoader } from '@/utilities/image';
import {
  logProductInterest,
  setAnalyticsUser,
  PODERCARD_PRODUCT_ID
} from "@separate/utilities/analytics";
import styles from '../podercard.module.scss';

export default function Landing(props) {
  const { isUserLoading, user } = props;
  const { t } = useTranslation();
  const { localePath } = useGetUrl();
  const ctaRef = React.useRef();
  const { locale } = useRouter();

  React.useEffect(() => {
    if (!user) return;

    setAnalyticsUser(user);
  }, [user]);

  const informationIcons = {
    banking: Banking,
    fast: Fast,
    group: Group,
    money: Money,
    security: Security
  };

  const aboutItems = [...t("podercard.about.items", { returnObjects: true })];
  const infromationItems = [...t("podercard.information.items", { returnObjects: true })];
  const testimonialItems = [...t("podercard.testimonials.items", { returnObjects: true })];
  const faqItems = [...t("podercard.faq.items", { returnObjects: true })];

  // IMPORTANT!
  // If the location of any of these files changes (highly unlikely since they are linked in the PODERcard app as well),
  // make sure to update KEEP_SUFFIX_FILES in .circleci/deploy.js
  const accountAgreementLink = `https://saberespoder.com/legal/PoderCard_DAA_${locale}.html`;
  const greenDotBankPrivacyPolicy = `https://saberespoder.com/legal/PoderCard_Privacy_Policy_${locale}.html`;
  const electronicCommunicationsAgreement = `https://saberespoder.com/legal/PoderCard_ECA_${locale}.html`;
  const greenDotTechnologyPrivacyStatement = `https://saberespoder.com${localePath}/legal/technology-privacy-statement`;
  const welcomeTechPrivacyPolicy = `https://saberespoder.com/legal/3.3.3022_WT_Privacy_Policy${locale === "en" ? "" : "_S"}`;
  const termsOfUseAgreement = `https://saberespoder.com${localePath}/legal/podercredit-terms-of-use-agreement`;

  function isPoderCardUser() {
    if(user) {
      return !!user.is_podercard_user;
    }
    return false;
  }

  function onClick() {
    if (user) logProductInterest(PODERCARD_PRODUCT_ID);
    window.open(
      "https://link.podercard.com/ToT2YHmIm5",
      "_blank"
    );
  }

  function IconComponent(icon) {
    const Icon = informationIcons[icon.name];
    return <Icon />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerWrapper}>
        <Row className={`${styles.main} justify-content-center`}>
          <Col md={12}>
            <div className={cx(styles.logoWrapper, "mb-4")}>
              <Image src={require("@images/podercard_gradient_logo.png")} alt="logo" loader={csLoader} />
            </div>
            <h1 className="mb-3">{t("podercard.main.title")}</h1>
            <p>{t("podercard.main.subtitle")}</p>
            <Button onClick={onClick} loading={isUserLoading}>
              {isPoderCardUser() ? t("podercard.main.ctaMember") : t("podercard.main.cta")}
            </Button>
          </Col>
        </Row>

        <Row className={styles.cta} ref={ctaRef}>
          <Col className="d-flex flex-column justify-content-end align-items-center">
            <Image
              loader={csLoader}
              alt="PODER card"
              src={require('@images/PODERcard.png')}
              height={331}
              width={326}
            />
            <Button className="mt-4 mb-5" onClick={onClick} loading={isUserLoading}>
              {isPoderCardUser() ? t("podercard.main.ctaMember") : t("podercard.main.cta")}
            </Button>
          </Col>
        </Row>
      </div>

      <Row className={`${styles.about} justify-content-center`}>
        <div className={styles.aboutItemsWrapper}>
          {aboutItems.map((item, index) => (
            <Col md={12} key={index} className={cx(styles.aboutItems, "mb-3")}>
              <h2>{item.title}</h2>
              <p>{item.text}</p>
            </Col>
          ))}
        </div>
        <Col md={12} className={cx(styles.imageColumnWrapper, "mb-3")}>
          <div className={styles.imageContainer}>
            <Image src="https://images.contentstack.io/v3/assets/blt224b6cd2a9d5dbf3/blt76f0c2505def3e5d/623dfba11188255deb1bb705/podercard_people.png" alt="people" loader={csLoader} layout="fill" />
          </div>
        </Col>
      </Row>

      <Row className={`${styles.information} py-4 px-2 justify-content-start justify-content-md-center`}>
        <Col md={12} xs={9}>
          <div className={styles.informationHeaderWrapper}>
            <div className={styles.line} />
            <h2 className={styles.header} dangerouslySetInnerHTML={{ __html: t("podercard.information.title") }} />
          </div>
        </Col>
        {infromationItems.map((item, index) => (
          <Col key={index} md={4} className={cx(styles.informationItems, "mb-4 d-flex flex-column")}>
            <div className={`${styles.iconContainer} d-flex justify-content-center align-items-center`}>
              <IconComponent name={item.key} />
            </div>
            <p dangerouslySetInnerHTML={{ __html: item.content }} className="mt-3" />
          </Col>
        ))}
      </Row>

      <Row className={`${styles.testimonials} py-4 px-2`}>
        <Col md={12} xs={12} className="mb-4">
          <div className={styles.testimonialsHeaderWrapper}>
            <div className={styles.line} />
            <h2 className={styles.header}>{t("podercard.testimonials.title")}</h2>
          </div>
        </Col>
        {testimonialItems.map((item, index) => (
          <Col key={index} md={4}>
            <StandardCard className={cx(styles.testimonialCard, "mb-4")} backgroundColor="#FFFFFF">
              <div className="d-flex justify-content-start align-items-center">
                <div className={`${styles.avatar}`}>
                  <Image src={item.image} alt="avatar" loader={csLoader} layout="fill" />
                </div>
                <div className="ms-4">
                  <strong className="d-block">{item.name}</strong>
                  <span className="d-block">{item.location}</span>
                </div>
                <div className="ms-auto mb-5">
                  <DoubleQuote />
                </div>
              </div>
              <p className="mb-0" dangerouslySetInnerHTML={{ __html: item.text }} />
            </StandardCard>
          </Col>
        ))}
      </Row>

      <Row className={cx(styles.faqWrapper, "py-4 px-0")}>
        <Col md={12} xs={12} className="mb-1">
          <div className={styles.faqHeaderWrapper}>
            <div className={styles.line} />
            <h2 className={styles.header}>{t("podercard.faq.title")}</h2>
          </div>
        </Col>
        <Col md={12} xs={12} className="px-0">
          <Accordion items={faqItems} toggleClassName={styles.accordionToggle} />
        </Col>
      </Row>

      <Row className="my-5 px-0">
        <Col md={12} xs={12} className="mb-1 text-center">
          <a  href={accountAgreementLink} target="_blank" rel="noopener noreferrer" className={styles.links}>
            {t('podercard.links.accountAgreement')}
          </a>

          <a  href={greenDotBankPrivacyPolicy} target="_blank" rel="noopener noreferrer" className={styles.links}>
            {t('podercard.links.greenDotBankPrivacyPolicy')}
          </a>

          <a href={electronicCommunicationsAgreement} target="_blank" rel="noopener noreferrer" className={styles.links}>
            {t('podercard.links.electronicCommunicationsAgreement')}
          </a>

          <Link href="legal/podercard-terms-and-conditions">
            <a target="_blank" rel="noopener noreferrer" className={styles.links}>
              {t('podercard.links.termsAndConditions')}
            </a>
          </Link>
        </Col>

        <Col md={12} xs={12} className="mb-1 text-center">
          <AnalyticsLink
            href={greenDotTechnologyPrivacyStatement}
            className={styles.links}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('podercard.links.greenDotTechnologyPrivacyStatement')}
          </AnalyticsLink>
          <AnalyticsLink
            href={welcomeTechPrivacyPolicy}
            className={styles.links}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('podercard.links.welcomeTechPrivacyPolicy')}
          </AnalyticsLink>
          <AnalyticsLink
            href={termsOfUseAgreement}
            className={styles.links}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('podercard.links.termsOfUseAgreement')}
          </AnalyticsLink>
        </Col>

        <Col md={12} xs={12} className={cx("text-center my-3 mx-0", styles.descriptions)}>
          <p>{t('podercard.descriptions.otherOffers')}</p>
          <p>{t('podercard.descriptions.deposit')}</p>
          <p>{t('podercard.descriptions.directDeposit')}</p>
          <p>{t('podercard.descriptions.freeATM')}</p>
          <p>{t('podercard.descriptions.disclosure')}</p>
        </Col>
      </Row>
    </div>
  );
}

Landing.propTypes = {
  isUserLoading: PropTypes.bool,
  user: PropTypes.object,
};
